import { QueryClient, useQueryClient } from "react-query";
import { getApiQueryKey, useGetApiData, usePostApiData } from "shared/datalayer";
import { GetRegisteredParticipantArgsType, InitiateRegReqType } from "./type";
import PublicApi from "../../../../shared/enums/PublicApi";
import RegApi from "../../enum/RegApi";
import CommonApi from "shared/enums/CommonApi";
import { getHttpStatusCode } from "../../../../shared/utils/network";
import {
  API_EXCEPTION_STATUS_CODE,
  API_FORBIDDEN_STATUS_CODE,
  API_SESSION_EXPIRE_STATUS_CODE
} from "../../../../shared/utils/constants";
import { LocalStorageKey } from "../../../../shared/utils/enums";
import LocalStorage from "../../../../shared/utils/storage/LocalStorage";
import Tokens from "../../../../shared/utils/storage/tokens";
import { RegFormApiResponseType } from "apps/registration/types/regFormApiResponse";
import { ApiRegistrationParticipantForParticipantResponse } from "../../../../shared/types/ApiRegistrationParticipantForParticipantResponse";
import { useNavigate } from "react-router-dom";
import { RegistrationRoute } from "../../routes/route";
import ApiBadgeDetails from "../../../../shared/types/ApiBadgeDetails";
import { ApiInvoicePaymentRequest } from "shared/types/ApiInvoicePaymentRequest";
import FieldAlias from "../../../../shared/form/renderer/enums/FieldAlias";

function getRegFormDetailsQueryKey(registrationFormId?: string, registrationType?: string) {
  return getApiQueryKey(PublicApi.REGISTRATION_FORM, {
    params: { registrationFormId, registrationType: registrationType || "" }
  });
}

/**
 * @name useGetFormDetails
 * @description This is used to get form detail based on formId
 * @param formId
 */
export function useGetFormDetails(formId?: string, registrationType?: string) {
  return useGetApiData<RegFormApiResponseType>(
    PublicApi.REGISTRATION_FORM,
    { params: { registrationFormId: formId, registrationType } },
    {
      enabled: Boolean(formId),
      onError: () => {},
      onSuccess: () => LocalStorage.removeItem(LocalStorageKey.TRANSIENT_TOKEN)
    }
  );
}

export async function updateWorkEmailAfterDomainValidation(
  queryClient: QueryClient,
  workEmail: string,
  formId: string
) {
  queryClient.setQueryData(getRegFormDetailsQueryKey(formId), (data: any) => {
    const existingData: RegFormApiResponseType = { ...(data as RegFormApiResponseType) };
    const profile = existingData.profile.map(field => {
      if (field.alias === FieldAlias.WORK_EMAIL) {
        return { ...field, value: workEmail, disabled: true };
      }
      return field;
    });
    return { ...existingData, profile };
  });
}

/**
 * @method useGetInvoicePaymentRequestDetails
 * @description This is used to get transaction details based on invoicePaymentRequestId & invoicePayerId
 * @param invoicePaymentRequestId
 * @param invoicePayerId
 */
export function useGetInvoicePaymentRequestDetails(invoicePaymentRequestId?: string, invoicePayerId?: string) {
  return useGetApiData<ApiInvoicePaymentRequest>(
    CommonApi.PAYMENT_BY_INVOICE_PAYER_GET_TRANSACTION_BY_ID,
    { params: { invoicePaymentRequestId, invoicePayerId } },
    { enabled: Boolean(invoicePaymentRequestId) && Boolean(invoicePayerId) }
  );
}

/**
 * @name useGetRegisteredParticipant
 * @description This is used to get detail of registered participant
 * @param registrationParticipantId
 * @param isEnabled
 * @param onSuccess
 */
export function useGetRegisteredParticipant({
  registrationParticipantId,
  isEnabled,
  onSuccess
}: GetRegisteredParticipantArgsType) {
  const navigate = useNavigate();
  return useGetApiData<ApiRegistrationParticipantForParticipantResponse>(
    RegApi.REGISTERED_PARTICIPANT_DETAIL,
    { params: { registrationParticipantId } },
    {
      enabled: isEnabled,
      onError: err => {
        if ([API_SESSION_EXPIRE_STATUS_CODE, API_FORBIDDEN_STATUS_CODE].includes(getHttpStatusCode(err))) {
          navigate(RegistrationRoute.Home, { replace: true });
        }
      },
      onSuccess: data => {
        onSuccess?.(data);
      }
    }
  );
}

export function useInitiateRegistration() {
  const queryClient = useQueryClient();
  return usePostApiData<ApiRegistrationParticipantForParticipantResponse, InitiateRegReqType>(PublicApi.INITIATE_REG, {
    onError: (err: any) => {
      if (getHttpStatusCode(err) === API_EXCEPTION_STATUS_CODE) {
        queryClient.invalidateQueries(PublicApi.CAPTCHA);
      }
    },
    onSuccess: (data: ApiRegistrationParticipantForParticipantResponse) => {
      Tokens.setTransitToken(data.transientToken);
    }
  });
}

export function useUpdateParticipation() {
  return usePostApiData<ApiRegistrationParticipantForParticipantResponse, InitiateRegReqType>(
    RegApi.UPDATE_PARTICIPANT_DETAIL
  );
}

export function useGetBadgeDetails(registrationParticipantId?: string) {
  return useGetApiData<Array<ApiBadgeDetails>>(
    RegApi.GET_BADGE_DETAILS,
    { params: { registrationParticipantId } },
    { enabled: Boolean(registrationParticipantId) }
  );
}
